import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { SommelierButton } from './SommelierButton';

interface RecommendationsItem {
  title: string;
  action?: JSX.Element | undefined;
  items: string[];
  icon: IconTypes;
}

interface Category {
  id: string | number;
  name: string;
  image: string;
}

interface DefaultSearchContentProps {
  recommendationsData: RecommendationsItem[];
  categories: Category[];
  handleSelectRecommendation: (
    value: string,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCategoryClick: (
    categoryId: string | number,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleHistoryReset: () => void;
  handleCloseDropdown: (
    e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => void;
}

export const DefaultSearchContent = observer<DefaultSearchContentProps>(
  ({
    recommendationsData,
    categories,
    handleSelectRecommendation,
    handleCategoryClick,
    handleHistoryReset,
    handleCloseDropdown,
  }) => {
    const { t } = useTranslation();

    return (
      <>
        {recommendationsData.map((section) => (
          <div key={section.title.toString()} className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <Typography.Text300Semibold className="text-navigation-text-primary">
                {section.title}
              </Typography.Text300Semibold>
              {section.action && (
                <button
                  onClick={handleHistoryReset}
                  className={cn('cursor-pointer border-none bg-transparent')}
                >
                  <Typography.Text200Regular className="text-navigation-text-tertiary">
                    {t('header:clear')}
                  </Typography.Text200Regular>
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {section.items.map((item) => (
                <button
                  key={item}
                  className="flex h-9 shrink-0 cursor-pointer flex-row items-center justify-start gap-1 rounded-full border bg-transparent pr-4 pl-3 transition-all duration-200 border-navigation-stroke-tertiary hover:border-navigation-stroke-secondary hover:bg-navigation-surface"
                  onClick={handleSelectRecommendation(item)}
                >
                  <Icon
                    type={section.icon}
                    size={16}
                    className="m-px shrink-0 text-navigation-icon-tertiary"
                  />
                  <Typography.Text200Medium className="text-navigation-text-primary">
                    {item}
                  </Typography.Text200Medium>
                </button>
              ))}
            </div>
          </div>
        ))}

        {categories.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <Typography.Text300Semibold className="text-navigation-text-primary">
                {t('header:categories')}
              </Typography.Text300Semibold>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className="flex cursor-pointer items-center gap-2 rounded-[8px] border py-1 pr-4 pl-1 transition-all duration-200 border-navigation-stroke-tertiary bg-navigation-bg hover:border-navigation-stroke-secondary hover:bg-navigation-surface"
                  onClick={handleCategoryClick(category.id)}
                >
                  <div className="flex h-12 w-12 flex-row items-center justify-center overflow-hidden rounded-[4px] bg-navigation-surface [&>img]:h-full [&>img]:w-full [&>img]:object-cover">
                    {category.image ? (
                      <CloudflareResponseImage
                        src={category.image}
                        alt={category.name}
                        srcParams={{
                          width: 60,
                          height: 60,
                          fit: 'contain',
                        }}
                        loading="lazy"
                      />
                    ) : (
                      <Icon
                        type="catalog-3.1"
                        size={24}
                        className="text-navigation-icon-tertiary"
                      />
                    )}
                  </div>
                  <Typography.Text200Medium className="text-navigation-text-primary">
                    {category.name}
                  </Typography.Text200Medium>
                </button>
              ))}
            </div>
          </div>
        )}

        {company.isSommelierEnabled && (
          <div className="flex flex-col gap-4">
            <div className="rounded-[16px] py-4 pr-4 pl-6 [background:linear-gradient(110deg,rgba(255,125,50,0.7)_-10.28%,rgba(174,16,93,0.7)_32.3%,rgba(93,15,39,0.7)_55.69%,rgba(38,5,11,0.7)_112.06%)]">
              <div className="flex flex-row items-center justify-start gap-4">
                <div className="flex flex-1 flex-col gap-1 text-navigation-text-primary">
                  <Typography.Text500Regular>
                    {t('header:imGenie')}
                  </Typography.Text500Regular>
                  <Typography.Text500Regular>
                    {t('header:personalAISommelier')}
                  </Typography.Text500Regular>
                </div>
                <SommelierButton onClick={handleCloseDropdown} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  },
);
